<template>
    <div :class="`dk-badge dk-badge--${size} d--inline-block`">
        <span :class="classes">
            <icon v-if="hasIcon" :name="options.icon" :color="options.color" class="m-right--xxs dk-badge__icon" />
            <slot></slot>
        </span>
    </div>
</template>

<script>
import Icon from '@v4/common/components/atoms/Icon';

/**
 * Standalone badge
 *
 * @version 4.0.0
 * @example ./examples/DkBadge.md
 */
export default {
    name: 'DkBadge',
    components: {
        Icon,
    },
    props: {
        /**
         * Based on this will be decided the color and the icon.
         *
         * Available values: `promo`, new`, `top_model_month`, `top_model_week`, `top_segment_month`
         * `top_segment_week`, `long_term_rental`, `super`, `superoffer`, `business`, `ending_soon`
         */
        name: {
            type: String,
            required: true,
            validator: (value) =>
                [
                    'promo',
                    'new',
                    'super',
                    'superoffer',
                    'business',
                    'ending_soon',
                    'top_model_month',
                    'top_model_week',
                    'top_segment_month',
                    'top_segment_week',
                    'long_term_rental',
                    'utility',
                    'classification',
                    'ranking',
                    'tag',
                    'offers_in_progress',
                    'best_seller',
                    'expiring_offers',
                    'not_available',
                    'benefits-count',
                    'benefits-count--outlined',
                    'stock',
                ].indexOf(value) > -1,
        },
        /**
         * Show or hide the icon.
         */
        hasIcon: {
            type: Boolean,
            default: true,
        },
        /**
         * Badge size. Available values: `sm`, `md`
         */
        size: {
            type: String,
            default: 'sm',
            validator: (value) => ['sm', 'md'].indexOf(value) > -1,
        },
    },
    computed: {
        options() {
            switch (this.name) {
                case 'expiring_offers':
                case 'not_available':
                    return {
                        color: 'error',
                    };
                case 'best_seller':
                    return {
                        color: 'primary',
                        variant: 'filled',
                    };
                case 'offers_in_progress':
                    return {
                        color: 'accent',
                    };
                case 'long_term_rental':
                case 'business':
                    return {
                        color: 'badge-turquoise',
                        variant: 'outlined',
                    };
                case 'new':
                    return {
                        color: 'badge-green',
                        variant: 'outlined',
                    };
                case 'top_model_month':
                    return {
                        icon: 'generic-trophy',
                        color: 'primary',
                        variant: 'outlined',
                    };
                case 'super':
                    return {
                        icon: 'generic-star',
                        color: 'badge-light-orange',
                        variant: 'outlined',
                    };
                case 'superoffer': {
                    return {
                        color: 'promo',
                        variant: 'filled',
                    };
                }
                case 'ending_soon': {
                    return {
                        color: 'badge-orange',
                        variant: 'outlined',
                    };
                }
                case 'utility':
                    return {
                        color: 'badge-turquoise',
                        variant: 'filled',
                    };
                case 'classification': {
                    return {
                        color: 'badge-light-orange',
                        variant: 'filled',
                    };
                }
                case 'ranking': {
                    return {
                        color: 'primary',
                        variant: 'filled',
                    };
                }
                case 'tag': {
                    return {
                        color: 'grey-200',
                        variant: 'filled',
                    };
                }
                case 'benefits-count': {
                    return {
                        color: 'primary-dark',
                        variant: 'filled',
                    };
                }
                case 'benefits-count--outlined': {
                    return {
                        color: 'primary-dark',
                        variant: 'outlined',
                    };
                }
                default:
                    return {
                        color: 'primary',
                        variant: 'outlined',
                    };
            }
        },
        classes() {
            let variantClasses = [];

            switch (this.options.variant) {
                case 'outlined':
                    variantClasses = [`color--${this.options.color}`, `border--${this.options.color}`];
                    break;
                case 'filled':
                    variantClasses = ['color--white', `bg--${this.options.color}`];
                    break;
                default:
                    variantClasses = [`color--${this.options.color}`, 'p-left--0', 'p-right--0'];
                    break;
            }

            return [
                'dk-badge__wrapper',
                'badge-stamp',
                'badge-stamp--inline',
                'badge-stamp--rounded',
                'p-top--xxs',
                'p-bottom--xxs',
                'p-left--xs',
                'p-right--xs',
                'v-align--middle',
                ...variantClasses,
            ];
        },
    },
};
</script>
